// GoalSetupModal.jsx
//
// Type-or-Speak sheet that captures monthly / yearly goals. Mirrors the
// Agenda voice flow in Briefing.jsx pixel-for-pixel: same live equalizer
// (<Waveform/>), same Listening / mm:ss / Transcribing… cadence.
//
// Flow:
//   1. Pick Type or Speak.
//   2. Speak: tee the mic into an AnalyserNode, recorder + analyser run in
//      parallel. Tap "Stop & review" → upload to Whisper → transcript lands
//      in the textarea so the user can edit before saving.
//   3. Type: free-form textarea (one per line OR a sentence works).
//   4. Save: hands the text up to the parent. parseGoals() turns natural
//      prose into a clean list of action-first goals via Claude.

import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.jsx';
import { Waveform } from './Waveform.jsx';
import { ProcessingOrb } from './ProcessingOrb.jsx';
import { transcribeAudio } from '../lib/llm.js';
import { AIConsentDeclinedError } from '../lib/aiConsent.js';
import { sfx } from '../lib/sfx.js';

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
  'audio/wav',
];

function pickMime() {
  if (!window.MediaRecorder?.isTypeSupported) return '';
  for (const t of MIME_CANDIDATES) {
    try { if (MediaRecorder.isTypeSupported(t)) return t; } catch {}
  }
  return '';
}

export function GoalSetupModal({
  open,
  title,
  subtitle,
  onSave,
  onClose,
  saving = false,
}) {
  const [mode, setMode] = useState('idle');     // idle | type | speak
  const [text, setText] = useState('');
  const [phase, setPhase] = useState('idle');   // idle | init | recording | transcribing | error
  const [elapsed, setElapsed] = useState(0);
  const [errMsg, setErrMsg] = useState('');
  // Live equalizer analyser — populated once we have a MediaStream.
  const [analyser, setAnalyser] = useState(null);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const startedAtRef = useRef(0);
  const cancelledRef = useRef(false);
  const finishingRef = useRef(false);
  // Web Audio graph for the live equalizer.
  const audioCtxRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const analyserNodeRef = useRef(null);

  // Reset everything every time the sheet is closed.
  useEffect(() => {
    if (!open) {
      setMode('idle');
      setText('');
      setPhase('idle');
      setElapsed(0);
      setErrMsg('');
      setAnalyser(null);
    }
  }, [open]);

  const teardown = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    try { recorderRef.current?.state === 'recording' && recorderRef.current.stop(); } catch {}
    try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch {}
    recorderRef.current = null;
    streamRef.current = null;
    // Tear down equalizer audio graph.
    try { sourceNodeRef.current?.disconnect(); } catch {}
    try { analyserNodeRef.current?.disconnect(); } catch {}
    try { audioCtxRef.current?.close?.(); } catch {}
    sourceNodeRef.current = null;
    analyserNodeRef.current = null;
    audioCtxRef.current = null;
    setAnalyser(null);
  };

  // Belt and suspenders: kill the mic if the modal unmounts mid-flow.
  useEffect(() => () => { cancelledRef.current = true; teardown(); }, []);

  const startRecording = async () => {
    setErrMsg('');
    setPhase('init');
    cancelledRef.current = false;
    finishingRef.current = false;
    setElapsed(0);

    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        throw new Error('Microphone API not available on this device.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (cancelledRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      streamRef.current = stream;

      // Live equalizer graph — tee'd off the same MediaStream as the
      // recorder so Whisper still gets untouched audio. Wrapped in try
      // because AudioContext can fail on older WebViews; equalizer is
      // decorative and should never block recording.
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          try { ctx.resume?.().catch(() => {}); } catch {}
          const source = ctx.createMediaStreamSource(stream);
          const node = ctx.createAnalyser();
          node.fftSize = 256;
          node.smoothingTimeConstant = 0.75;
          node.minDecibels = -85;
          node.maxDecibels = -20;
          source.connect(node);
          // Do NOT connect the analyser to ctx.destination — that would
          // route the mic to the speaker and feedback-loop.
          audioCtxRef.current = ctx;
          sourceNodeRef.current = source;
          analyserNodeRef.current = node;
          setAnalyser(node);
        }
      } catch { /* equalizer is decorative; ignore */ }

      const mimeType = pickMime();
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch {}
        streamRef.current = null;
        if (cancelledRef.current) return;

        const type = chunksRef.current[0]?.type || rec.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        if (!blob.size) {
          setErrMsg("I didn't catch any audio. Try again?");
          setPhase('error');
          return;
        }

        setPhase('transcribing');
        sfx.startTranscribing && sfx.startTranscribing();
        try {
          const transcript = await transcribeAudio(blob);
          if (cancelledRef.current) return;
          if (!transcript) {
            setErrMsg("I couldn't make out any speech. Try again?");
            setPhase('error');
            return;
          }
          // Append to whatever was typed so the user can dictate multiple
          // chunks into the same textarea.
          setText(prev => (prev ? prev.trimEnd() + '\n' + transcript : transcript));
          setPhase('idle');
          setMode('type');           // drop into the editable surface
          sfx.ready && sfx.ready();
        } catch (err) {
          if (cancelledRef.current) return;
          if (err instanceof AIConsentDeclinedError) { setPhase('idle'); return; }
          setErrMsg(err?.message || 'Transcription failed. Try again?');
          setPhase('error');
        } finally {
          sfx.stopTranscribing && sfx.stopTranscribing();
        }
      };

      rec.start();
      startedAtRef.current = Date.now();
      tickRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
      setPhase('recording');
      sfx.recordStart && sfx.recordStart();
    } catch (err) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Microphone access was denied. Enable it in Settings to use voice input.'
        : (err?.message || 'Could not access the microphone.');
      setErrMsg(msg);
      setPhase('error');
    }
  };

  const stopAndTranscribe = () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    sfx.toast && sfx.toast();
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    try { recorderRef.current?.stop(); } catch {}
  };

  const cancelVoice = () => {
    cancelledRef.current = true;
    sfx.sheetClose && sfx.sheetClose();
    teardown();
    setPhase('idle');
    setMode('idle');
  };

  const retry = () => {
    setErrMsg('');
    cancelledRef.current = false;
    finishingRef.current = false;
    setElapsed(0);
    teardown();
    startRecording();
  };

  const handleSave = () => {
    const v = text.trim();
    if (!v) return;
    onSave(v);
  };

  if (!open) return null;

  const canSave = text.trim().length > 0 && !saving && phase !== 'transcribing' && phase !== 'recording';

  const mm = String(Math.floor(elapsed / 60)).padStart(1, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  // Should we show the editor surface (textarea + add-by-voice + clear)?
  const showEditor =
    (mode === 'type' || (mode === 'speak' && phase === 'idle')) &&
    phase !== 'recording' && phase !== 'transcribing' && phase !== 'init' &&
    !saving;

  // Should we show the voice surface (waveform + listening / transcribing /
  // error)?
  const showVoiceSurface = phase === 'init' || phase === 'recording' || phase === 'transcribing' || phase === 'error';

  return (
    <>
      <div
        onClick={() => (saving ? null : onClose())}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
          zIndex: 200, animation: 'fadeIn 140ms ease-out',
        }}
      />
      {/* Flex-centered wrapper. Splitting the centering (here) from the
          entrance animation (on the child) avoids the bug where
          `sheetIn` overwrites a `translate(-50%, -50%)` centering
          transform mid-keyframe, making the sheet flash at the
          top-left corner for one frame before snapping into place. */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 201,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
          pointerEvents: 'none',
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          style={{
            width: '100%',
            maxWidth: 480,
            maxHeight: '85vh',
            background: 'rgba(20,24,32,0.98)',
            border: '1px solid var(--stroke-1)',
            borderRadius: 22,
            padding: '22px 22px 18px',
            overflowY: 'auto',
            boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
            animation: 'sheetIn 220ms cubic-bezier(0.22,1,0.36,1)',
            pointerEvents: 'auto',
          }}
        >
        <div style={{
          fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 19,
          color: 'var(--fg-1)', letterSpacing: '-0.01em',
        }}>
          {title}
        </div>
        {subtitle && (
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--fg-3)',
            lineHeight: 1.5, marginTop: 6,
          }}>
            {subtitle}
          </div>
        )}

        {/* Saving surface — parent is parsing goals + persisting. Shown
            instead of the editor / footer so the user sees what's
            happening rather than a frozen "Saving…" button. SFX runs
            from the parent (sfx.startSaving / stopSaving). */}
        {saving && (
          <div style={{
            marginTop: 22,
            padding: '24px 18px',
            borderRadius: 18,
            background: 'rgba(45,156,255,0.06)',
            border: '1px solid rgba(45,156,255,0.22)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            <ProcessingOrb
              size={68}
              label="Saving your goals"
              sublabel="Tightening these into clean, action-first goals."
            />
          </div>
        )}

        {/* Mode picker — Speak is the primary CTA (blue gradient) since
            voice is the headline path for goal capture. Type stays as
            the ghost-pill alternative. */}
        {mode === 'idle' && !saving && (
          <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'center' }}>
            <button
              onClick={() => { sfx.nav && sfx.nav(); setMode('speak'); startRecording(); }}
              className="btn btn-primary"
              style={{
                padding: '11px 22px', borderRadius: 999,
                gap: 8, display: 'inline-flex', alignItems: 'center',
              }}
            >
              <Icon name="mic" size={14} color="#fff"/> Speak
            </button>
            <button
              onClick={() => { sfx.nav && sfx.nav(); setMode('type'); }}
              className="btn"
              style={{
                padding: '11px 22px', borderRadius: 999,
                gap: 8, display: 'inline-flex', alignItems: 'center',
                background: 'rgba(255,255,255,0.06)', color: 'var(--fg-1)',
                border: '1px solid var(--stroke-2)',
              }}
            >
              <Icon name="clipboard" size={14}/> Type
            </button>
          </div>
        )}

        {/* Voice surface — equalizer when listening, processing-orb when
            transcribing, error message when something went wrong. */}
        {showVoiceSurface && (
          <div style={{
            marginTop: 22,
            padding: '20px 18px 16px',
            borderRadius: 18,
            background: 'rgba(45,156,255,0.06)',
            border: '1px solid rgba(45,156,255,0.22)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
          }}>
            {phase === 'transcribing' ? (
              // Whisper is running. Swap the equalizer for a proper
              // loading visual + ambient SFX is already firing from the
              // recorder.onstop handler.
              <ProcessingOrb
                size={64}
                label="Transcribing"
                sublabel="Turning your voice into text."
              />
            ) : (
              <>
                <div style={{ width: '100%', maxWidth: 260, display: 'flex', justifyContent: 'center' }}>
                  <Waveform analyser={analyser}/>
                </div>

                <div style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--fg-3)',
                  textAlign: 'center', minHeight: 32, lineHeight: 1.45,
                }}>
                  {phase === 'init' && (
                    <span style={mono}>Waking up the mic…</span>
                  )}
                  {phase === 'recording' && (
                    <div>
                      <div style={mono}>Listening</div>
                      <div style={{ ...mono, fontSize: 22, color: 'var(--fg-1)', marginTop: 4 }}>
                        {mm}:{ss}
                      </div>
                    </div>
                  )}
                  {phase === 'error' && (
                    <span style={{ color: '#FF8699' }}>{errMsg}</span>
                  )}
                </div>
              </>
            )}

            {phase === 'recording' && (
              <button
                onClick={stopAndTranscribe}
                style={{
                  padding: '11px 26px', borderRadius: 999,
                  background: 'linear-gradient(180deg, #47A6FF, #2D7DE0)',
                  border: '1px solid rgba(71,166,255,0.55)',
                  color: '#ffffff',
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5,
                  cursor: 'pointer',
                  boxShadow: '0 8px 22px rgba(45,125,224,0.4)',
                }}
              >
                Stop &amp; review
              </button>
            )}
            {phase === 'error' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={cancelVoice} style={ghostBtn}>Cancel</button>
                <button
                  onClick={retry}
                  style={{
                    padding: '9px 18px', borderRadius: 999,
                    background: 'linear-gradient(180deg, #47A6FF, #2D7DE0)',
                    border: '1px solid rgba(71,166,255,0.55)',
                    color: '#ffffff',
                    fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12.5,
                    cursor: 'pointer',
                  }}
                >
                  Try again
                </button>
              </div>
            )}
            {(phase === 'init' || phase === 'recording' || phase === 'transcribing') && (
              <button
                onClick={cancelVoice}
                style={{
                  padding: '6px 14px', borderRadius: 8,
                  background: 'transparent', border: 'none',
                  color: 'var(--fg-4)', cursor: 'pointer',
                  fontFamily: 'var(--font-body)', fontSize: 12,
                }}
              >Cancel</button>
            )}
          </div>
        )}

        {/* Editor surface (text mode, or after transcription completes) */}
        {showEditor && (
          <div style={{ marginTop: 18 }}>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="One goal per line, or just say it in a sentence."
              rows={6}
              style={{
                width: '100%', resize: 'vertical', minHeight: 120,
                padding: '12px 14px',
                borderRadius: 14,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid var(--stroke-1)',
                color: 'var(--fg-1)',
                fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.5,
                outline: 'none',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button
                onClick={() => { sfx.nav && sfx.nav(); setMode('speak'); startRecording(); }}
                style={ghostBtn}
              >
                <Icon name="mic" size={13}/>
                Add by voice
              </button>
              {text && (
                <button
                  onClick={() => { sfx.nav && sfx.nav(); setText(''); }}
                  style={ghostBtn}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Footer — Cancel + Save my goals. Hidden during voice phases so
            it doesn't compete with the Stop & review CTA, and during the
            saving orb so the user can't double-click. */}
        {!showVoiceSurface && !saving && (
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button
              onClick={() => { sfx.sheetClose && sfx.sheetClose(); onClose(); }}
              disabled={saving}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 999,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid var(--stroke-1)',
                color: 'var(--fg-2)',
                fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13.5,
                cursor: saving ? 'default' : 'pointer',
                opacity: saving ? 0.5 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Cancel'}
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                flex: 1.4, padding: '12px 16px', borderRadius: 999,
                background: canSave ? 'linear-gradient(180deg, #47A6FF, #2D7DE0)' : 'rgba(255,255,255,0.06)',
                border: canSave ? '1px solid rgba(71,166,255,0.55)' : '1px solid var(--stroke-1)',
                color: canSave ? '#ffffff' : 'var(--fg-3)',
                fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5,
                cursor: canSave ? 'pointer' : 'default',
                boxShadow: canSave ? '0 8px 22px rgba(45,125,224,0.4)' : 'none',
              }}
            >
              {saving ? 'Saving…' : 'Save my goals'}
            </button>
          </div>
        )}
        </div>
      </div>
    </>
  );
}

const mono = {
  fontFamily: 'ui-monospace, SF Mono, Menlo, Consolas, monospace',
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--accent-hi)',
};

const ghostBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 12px', borderRadius: 999,
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--stroke-1)',
  color: 'var(--fg-2)',
  fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12,
  cursor: 'pointer',
};

const pickerBtn = (accent) => ({
  flex: 1,
  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
  padding: '14px 14px',
  borderRadius: 14,
  background: accent
    ? 'linear-gradient(180deg, rgba(71,166,255,0.16), rgba(45,125,224,0.06))'
    : 'rgba(255,255,255,0.05)',
  border: accent
    ? '1px solid rgba(71,166,255,0.4)'
    : '1px solid var(--stroke-1)',
  color: accent ? 'var(--accent-hi)' : 'var(--fg-2)',
  cursor: 'pointer',
});
const pickerLabel = {
  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14,
  color: 'var(--fg-1)',
};
const pickerHint = {
  fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--fg-3)',
};
